import { useCallback, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAtom, useAtomValue } from 'jotai'

import {
  ExcelPlanResponse,
  StaffService,
  StaffMember,
  ShiftPresetData_Input,
  DetailedOptimizerService,
  DetailedOptimizerResponse,
  CellWriteInstruction_Output,
  Product_Input,
  ProductionTask_Input,
  FilesService,
  ProductionPlanService,
  LatestShiftData_Input,
} from '@/api'
import { excelPlanResponseAtom } from '@/atoms/optimization'
import { toastAtom } from '@/atoms/toastAtom'

import { OPERATOR_MACHINE_ID } from '../components/staffUtils'

const WEEKDAY_LABELS = ['日', '月', '火', '水', '木', '金', '土'] as const

// シフト（day/evening/night/operator）ごとの割当可能なスタッフ名
type StaffNamesByShift = Record<'day' | 'evening' | 'night' | 'operator', Set<string>>

// サニタイズで除去したスタッフ名。deleted: staffList に存在しない / offShift: 所属シフトが異なる
interface RemovedStaff {
  deleted: Set<string>
  offShift: Set<string>
}

// 当該シフトに所属しないスタッフ名を null へ置換する。除去した名前は removed に収集。
// 所属シフトを変更したスタッフが旧シフトに残っていると、画面には表示されないまま
// 同日の割当件数（2件まで）に数えられてしまうため除去する。
const sanitizeShiftRecord = (
  record: Record<string, (string | null)[]> | undefined,
  shiftNames: Set<string>,
  namesByShift: StaffNamesByShift,
  allNames: Set<string>,
  removed: RemovedStaff,
): Record<string, (string | null)[]> => {
  const out: Record<string, (string | null)[]> = {}
  for (const [machineId, slots] of Object.entries(record ?? {})) {
    const arr = Array.isArray(slots) ? slots : [slots ?? null, null]
    const allowed = machineId === OPERATOR_MACHINE_ID ? namesByShift.operator : shiftNames
    out[machineId] = arr.map((name) => {
      if (typeof name !== 'string') return null
      if (allowed.has(name)) return name
      if (allNames.has(name)) {
        removed.offShift.add(name)
      } else {
        removed.deleted.add(name)
      }
      return null
    })
  }
  return out
}

// day/evening/night の3レコードをサニタイズし、除去した名前一覧を返す。
const sanitizeShiftData = (
  data: {
    day?: Record<string, (string | null)[]>
    evening?: Record<string, (string | null)[]>
    night?: Record<string, (string | null)[]>
  },
  namesByShift: StaffNamesByShift,
): {
  day: Record<string, (string | null)[]>
  evening: Record<string, (string | null)[]>
  night: Record<string, (string | null)[]>
  removed: RemovedStaff
} => {
  const removed: RemovedStaff = { deleted: new Set(), offShift: new Set() }
  const allNames = new Set(Object.values(namesByShift).flatMap((names) => [...names]))
  return {
    day: sanitizeShiftRecord(data.day, namesByShift.day, namesByShift, allNames, removed),
    evening: sanitizeShiftRecord(data.evening, namesByShift.evening, namesByShift, allNames, removed),
    night: sanitizeShiftRecord(data.night, namesByShift.night, namesByShift, allNames, removed),
    removed,
  }
}

const emptyRemovedStaff = (): RemovedStaff => ({ deleted: new Set(), offShift: new Set() })

const mergeRemovedStaff = (into: RemovedStaff, from: RemovedStaff): void => {
  for (const n of from.deleted) into.deleted.add(n)
  for (const n of from.offShift) into.offShift.add(n)
}

// 計画開始日の直前に何を流していたか（生産順・段替え判定の起点）を渡すための遡り日数
const PAST_TASK_LOOKBACK_DAYS = 14

// 計画開始日より前の生産タスクを直近 PAST_TASK_LOOKBACK_DAYS 日分だけ抽出する
const collectPastTasks = (
  tasks: { date: string }[],
  startDate: string,
): ProductionTask_Input[] => {
  const from = new Date(startDate)
  from.setDate(from.getDate() - PAST_TASK_LOOKBACK_DAYS)
  const fromDate = from.toISOString().slice(0, 10)
  return tasks.filter((t) => t.date < startDate && t.date >= fromDate) as unknown as ProductionTask_Input[]
}

// 2つのシフト割当を正規化して比較する（欠損キー・末尾のnull・undefined は同一扱い）。
// 最適化結果に反映されていない割当変更があるかの判定に使う。
export const shiftAssignmentsEqual = (
  a: Record<string, Record<string, Record<string, (string | null)[]>>>,
  b: Record<string, Record<string, Record<string, (string | null)[]>>>,
): boolean => {
  const normalizeSlots = (slots: (string | null)[] | undefined): string[] => {
    const out = (slots ?? []).map((v) => (typeof v === 'string' ? v : ''))
    while (out.length > 0 && out[out.length - 1] === '') out.pop()
    return out
  }
  const dates = new Set([...Object.keys(a), ...Object.keys(b)])
  for (const date of dates) {
    const shifts = new Set([...Object.keys(a[date] ?? {}), ...Object.keys(b[date] ?? {})])
    for (const shift of shifts) {
      const machines = new Set([
        ...Object.keys(a[date]?.[shift] ?? {}),
        ...Object.keys(b[date]?.[shift] ?? {}),
      ])
      for (const machineId of machines) {
        const sa = normalizeSlots(a[date]?.[shift]?.[machineId])
        const sb = normalizeSlots(b[date]?.[shift]?.[machineId])
        if (sa.length !== sb.length) return false
        for (let i = 0; i < sa.length; i++) {
          if (sa[i] !== sb[i]) return false
        }
      }
    }
  }
  return true
}

export type ShiftType = 'day' | 'evening' | 'night'
export const SHIFT_LABELS: Record<ShiftType, string> = {
  day: '昼',
  evening: '夕',
  night: '夜',
}

export interface WeekOption {
  label: string
  startDate: string
  endDate: string
  dates: string[]
}

// date → shift → machine_id → [staff1, staff2] (up to 2 staff per slot)
export type ShiftAssignments = Record<string, Record<ShiftType, Record<string, (string | null)[]>>>

export const MACHINE_ORDER = ['J850AD-1', 'J850AD-2', 'J650AD-1', 'J650AD-2', 'J550AD', 'J450AD', 'αS300', '100EVA']

export type ExcelReloadStep = 'downloading' | 'reading' | 'optimizing'
export type ExcelReloadStage = ExcelReloadStep | 'idle' | 'success' | 'error'
export type ExcelReloadStatus = 'pending' | 'active' | 'completed' | 'error'

export const excelReloadSteps: { id: ExcelReloadStep; label: string }[] = [
  { id: 'downloading', label: 'OneDriveから最新ファイルを取得' },
  { id: 'reading', label: 'Excelデータを再読み込み' },
  { id: 'optimizing', label: '最適化を再実行' },
]

export interface ExcelReloadDialogState {
  visible: boolean
  open: () => void
  close: () => void
  progress: { id: ExcelReloadStep; label: string; status: ExcelReloadStatus }[]
  stage: ExcelReloadStage
  error: string | null
  isExcelReloading: boolean
}

export interface ExcelWriteDialogState {
  visible: boolean
  open: (instructions: CellWriteInstruction_Output[]) => void
  close: () => void
  isWriting: boolean
  isLocked: boolean
  isSuccess: boolean
  progress: number | null
  lastLog: string | null
  error: string | null
}

export const useDetailedPlanFlow = () => {
  const [stepIndex, setStepIndex] = useState(0)
  const [excelPlanResponse, setExcelPlanResponse] = useAtom(excelPlanResponseAtom)
  const [selectedWeek, setSelectedWeek] = useState<WeekOption | null>(null)
  const [assignments, setAssignments] = useState<ShiftAssignments>({})
  const [optimizerResult, setOptimizerResult] = useState<DetailedOptimizerResponse | null>(null)
  // 直近の最適化に渡したシフト割当。結果表示画面で割当を修正した際、再最適化が必要かの判定に使う。
  const [optimizedAssignments, setOptimizedAssignments] = useState<ShiftAssignments | null>(null)
  const [isOptimizing, setIsOptimizing] = useState(false)
  const toast = useAtomValue(toastAtom)

  // Excel write dialog state
  const [excelWriteModalVisible, setExcelWriteModalVisible] = useState(false)
  const [excelWriteIsWriting, setExcelWriteIsWriting] = useState(false)
  const [excelWriteIsLocked, setExcelWriteIsLocked] = useState(false)
  const [excelWriteIsSuccess, setExcelWriteIsSuccess] = useState(false)
  const [excelWriteProgress, setExcelWriteProgress] = useState<number | null>(null)
  const [excelWriteLastLog, setExcelWriteLastLog] = useState<string | null>(null)
  const [excelWriteError, setExcelWriteError] = useState<string | null>(null)

  const resetExcelWriteState = useCallback(() => {
    setExcelWriteIsWriting(false)
    setExcelWriteIsLocked(false)
    setExcelWriteIsSuccess(false)
    setExcelWriteProgress(null)
    setExcelWriteLastLog(null)
    setExcelWriteError(null)
  }, [])

  const closeExcelWriteModal = useCallback(() => {
    if (excelWriteIsWriting) return
    setExcelWriteModalVisible(false)
    resetExcelWriteState()
  }, [excelWriteIsWriting, resetExcelWriteState])

  const writeToExcel = useCallback(async (instructions: CellWriteInstruction_Output[]) => {
    resetExcelWriteState()
    setExcelWriteModalVisible(true)
    setExcelWriteIsWriting(true)
    setExcelWriteLastLog('書き込みを開始しています...')

    // Build products map for machine-sheet lookup
    const productsMap: Record<string, Product_Input> = {}
    for (const p of excelPlanResponse?.echoOrderProducts ?? []) {
      productsMap[p.sku_id] = p as unknown as Product_Input
    }

    try {
      const result = await DetailedOptimizerService.writeDetailedOptimizer({
        cell_instructions: instructions,
        scheduled_tasks: optimizerResult?.scheduled_tasks ?? [],
        products: productsMap,
      })
      setExcelWriteIsSuccess(true)
      setExcelWriteProgress(100)
      setExcelWriteLastLog(`完了: ${result.written_cells}件のセルを書き込みました`)
      toast?.show({ severity: 'success', summary: '書き戻し完了', detail: `${result.written_cells}件のセルをExcelに書き戻しました`, life: 3000 })
    } catch (error) {
      const err = error as { status?: number; body?: { detail?: string }; message?: string }
      const isLocked = err.status === 423
      const message = err.body?.detail ?? err.message ?? '不明なエラーが発生しました'
      setExcelWriteIsLocked(isLocked)
      setExcelWriteError(message)
      setExcelWriteLastLog(null)
      toast?.show({ severity: 'error', summary: 'Excel書き戻し失敗', detail: message, life: 5000 })
    } finally {
      setExcelWriteIsWriting(false)
    }
  }, [resetExcelWriteState, toast, optimizerResult, excelPlanResponse])

  // Excel reload dialog state
  const [excelReloadModalVisible, setExcelReloadModalVisible] = useState(false)
  const [excelReloadStage, setExcelReloadStage] = useState<ExcelReloadStage>('idle')
  const [excelReloadFailedStep, setExcelReloadFailedStep] = useState<ExcelReloadStep | null>(null)
  const [excelReloadError, setExcelReloadError] = useState<string | null>(null)

  const isExcelReloading = excelReloadStage === 'downloading' || excelReloadStage === 'reading' || excelReloadStage === 'optimizing'

  const resetExcelReloadState = useCallback(() => {
    setExcelReloadStage('idle')
    setExcelReloadFailedStep(null)
    setExcelReloadError(null)
  }, [])

  const closeExcelReloadModal = useCallback(() => {
    if (isExcelReloading) return
    setExcelReloadModalVisible(false)
    resetExcelReloadState()
  }, [isExcelReloading, resetExcelReloadState])

  const getReloadStepStatus = useCallback(
    (step: ExcelReloadStep): ExcelReloadStatus => {
      if (excelReloadStage === 'success') return 'completed'
      if (excelReloadStage === 'error') {
        if (excelReloadFailedStep === step) return 'error'
        if (!excelReloadFailedStep) return 'pending'
        const failedIndex = excelReloadSteps.findIndex((s) => s.id === excelReloadFailedStep)
        const stepIndex = excelReloadSteps.findIndex((s) => s.id === step)
        return stepIndex < failedIndex ? 'completed' : 'pending'
      }
      const currentIndex = excelReloadSteps.findIndex((s) => s.id === excelReloadStage)
      const stepIndex = excelReloadSteps.findIndex((s) => s.id === step)
      if (currentIndex === -1) return 'pending'
      if (currentIndex > stepIndex) return 'completed'
      if (currentIndex === stepIndex) return 'active'
      return 'pending'
    },
    [excelReloadStage, excelReloadFailedStep],
  )

  const excelReloadProgress = useMemo(
    () => excelReloadSteps.map((step) => ({ ...step, status: getReloadStepStatus(step.id) })),
    [getReloadStepStatus],
  )

  const staffQuery = useQuery({
    queryKey: ['staff'],
    queryFn: () => StaffService.getStaff(),
    refetchOnWindowFocus: false,
  })


  const staffList: StaffMember[] = useMemo(
    () => staffQuery.data?.staff ?? [],
    [staffQuery.data],
  )

  // 現在有効なスタッフ名の集合（staff.json 由来）。削除済みスタッフのクリーンアップに使用。
  const staffNamesByShift = useMemo<StaffNamesByShift>(() => {
    const out: StaffNamesByShift = { day: new Set(), evening: new Set(), night: new Set(), operator: new Set() }
    for (const s of staffList) {
      out[s.shift]?.add(s.name)
    }
    return out
  }, [staffList])

  const steps = useMemo(
    () => [
      { label: 'Excelデータ読み込み' },
      { label: '週の選択' },
      { label: 'シフト割当' },
      { label: '最適化結果' },
    ],
    [],
  )

  // Extract unique machine IDs from production tasks with custom order
  const machineIds = useMemo(() => {
    if (!excelPlanResponse) return []
    const ids = new Set<string>()
    for (const task of excelPlanResponse.echoProductionTasks ?? []) {
      if (task.machine_id) ids.add(task.machine_id)
    }
    return Array.from(ids).sort((a, b) => {
      const ai = MACHINE_ORDER.indexOf(a)
      const bi = MACHINE_ORDER.indexOf(b)
      const aIdx = ai === -1 ? MACHINE_ORDER.length : ai
      const bIdx = bi === -1 ? MACHINE_ORDER.length : bi
      if (aIdx !== bIdx) return aIdx - bIdx
      return a.localeCompare(b)
    })
  }, [excelPlanResponse])

  // Group dates into weeks (Monday-Sunday)
  const weekOptions = useMemo((): WeekOption[] => {
    if (!excelPlanResponse?.dates?.length) return []
    const dates = [...excelPlanResponse.dates].sort()
    const weeks: WeekOption[] = []
    let currentWeekDates: string[] = []
    let currentWeekStart: Date | null = null

    for (const dateStr of dates) {
      const d = new Date(dateStr)
      const dayOfWeek = d.getDay()
      // Monday = 1, get the Monday of this week
      const monday = new Date(d)
      monday.setDate(d.getDate() - ((dayOfWeek + 6) % 7))
      const mondayStr = monday.toISOString().slice(0, 10)

      if (!currentWeekStart || mondayStr !== currentWeekStart.toISOString().slice(0, 10)) {
        if (currentWeekDates.length > 0 && currentWeekStart) {
          const sunday = new Date(currentWeekStart)
          sunday.setDate(currentWeekStart.getDate() + 6)
          weeks.push({
            label: `${currentWeekStart.toISOString().slice(5, 10)} 〜 ${sunday.toISOString().slice(5, 10)}`,
            startDate: currentWeekStart.toISOString().slice(0, 10),
            endDate: sunday.toISOString().slice(0, 10),
            dates: currentWeekDates,
          })
        }
        currentWeekStart = monday
        currentWeekDates = []
      }
      currentWeekDates.push(dateStr)
    }

    if (currentWeekDates.length > 0 && currentWeekStart) {
      const sunday = new Date(currentWeekStart)
      sunday.setDate(currentWeekStart.getDate() + 6)
      weeks.push({
        label: `${currentWeekStart.toISOString().slice(5, 10)} 〜 ${sunday.toISOString().slice(5, 10)}`,
        startDate: currentWeekStart.toISOString().slice(0, 10),
        endDate: sunday.toISOString().slice(0, 10),
        dates: currentWeekDates,
      })
    }

    return weeks
  }, [excelPlanResponse?.dates])

  const goToStep = useCallback((index: number) => {
    setStepIndex(index)
  }, [])

  const loadExcel = useCallback(
    (data: ExcelPlanResponse) => {
      setExcelPlanResponse(data)
      setStepIndex(1)
    },
    [setExcelPlanResponse],
  )

  // 除去された削除済み・所属シフト外のスタッフ名をトーストで通知する。
  const notifyRemovedStaff = useCallback(
    (removed: RemovedStaff) => {
      if (removed.deleted.size > 0) {
        toast?.show({
          severity: 'warn',
          summary: '削除済みスタッフを解除',
          detail: `削除されたスタッフ（${[...removed.deleted].join('、')}）をシフトから解除しました`,
          life: 4000,
        })
      }
      if (removed.offShift.size > 0) {
        toast?.show({
          severity: 'warn',
          summary: '所属シフト外のスタッフを解除',
          detail: `所属シフトと異なる枠に入っていたスタッフ（${[...removed.offShift].join('、')}）を解除しました`,
          life: 4000,
        })
      }
    },
    [toast],
  )

  const selectWeek = useCallback(
    async (week: WeekOption) => {
      setSelectedWeek(week)
      const init: ShiftAssignments = {}
      for (const date of week.dates) {
        init[date] = { day: {}, evening: {}, night: {} }
      }
      // staffList が未ロードの場合はフィルタをスキップ（誤って全消去しないため）
      const shouldFilter = staffList.length > 0
      const removedNames = emptyRemovedStaff()
      try {
        const result = await StaffService.getLatestShift()
        const weekdays = result?.data?.weekdays ?? {}
        for (const date of week.dates) {
          const d = new Date(date)
          const label = WEEKDAY_LABELS[d.getDay()]
          const preset = weekdays[label]
          if (preset) {
            if (shouldFilter) {
              const sanitized = sanitizeShiftData(preset, staffNamesByShift)
              init[date] = { day: sanitized.day, evening: sanitized.evening, night: sanitized.night }
              mergeRemovedStaff(removedNames, sanitized.removed)
            } else {
              init[date] = {
                day: { ...preset.day },
                evening: { ...preset.evening },
                night: { ...preset.night },
              }
            }
          }
        }
      } catch {
        // latest_shift.json が未作成の場合はそのまま空で進む
      }
      setAssignments(init)
      setStepIndex(2)
      notifyRemovedStaff(removedNames)
    },
    [staffList.length, staffNamesByShift, notifyRemovedStaff],
  )

  const updateAssignment = useCallback(
    (date: string, shift: ShiftType, machineId: string, slotIndex: number, staffName: string | null) => {
      setAssignments((prev) => {
        const current = prev[date]?.[shift]?.[machineId] ?? [null, null]
        const updated = [...current]
        updated[slotIndex] = staffName
        return {
          ...prev,
          [date]: {
            ...prev[date],
            [shift]: {
              ...prev[date]?.[shift],
              [machineId]: updated,
            },
          },
        }
      })
    },
    [],
  )

  // 成形機×日の3シフト分の割当をまとめて置き換える（最適化結果画面の「スタッフ割当の修正」用）
  const updateMachineDayAssignments = useCallback(
    (date: string, machineId: string, slotsByShift: Record<ShiftType, (string | null)[]>) => {
      setAssignments((prev) => {
        const dateEntry = prev[date] ?? { day: {}, evening: {}, night: {} }
        const next = { ...dateEntry }
        for (const shift of ['day', 'evening', 'night'] as ShiftType[]) {
          next[shift] = { ...dateEntry[shift], [machineId]: [...slotsByShift[shift]] }
        }
        return { ...prev, [date]: next }
      })
    },
    [],
  )

  // 最適化後にシフト割当が変更されたか（結果が古くなっている可能性）
  const hasPendingShiftChanges = useMemo(
    () => optimizedAssignments != null && !shiftAssignmentsEqual(assignments, optimizedAssignments),
    [assignments, optimizedAssignments],
  )

  const buildLatestShiftData = useCallback((): LatestShiftData_Input => {
    const weekdays: Record<string, ShiftPresetData_Input> = {}
    for (const [dateStr, shifts] of Object.entries(assignments)) {
      const d = new Date(dateStr)
      const label = WEEKDAY_LABELS[d.getDay()]
      weekdays[label] = {
        day: shifts.day ?? {},
        evening: shifts.evening ?? {},
        night: shifts.night ?? {},
      }
    }
    return { weekdays }
  }, [assignments])

  const runOptimizer = useCallback(async () => {
    if (!excelPlanResponse || !selectedWeek) return

    setIsOptimizing(true)
    try {
      // Save current assignments as latest shift (best-effort)
      try {
        await StaffService.saveLatestShift({ data: buildLatestShiftData() })
      } catch {
        // non-critical
      }

      const productsMap: Record<string, Product_Input> = {}
      for (const p of excelPlanResponse.echoOrderProducts ?? []) {
        productsMap[p.sku_id] = p as unknown as Product_Input
      }

      // 最終防御: 手動で残ってしまった削除済みスタッフを optimizer 送信前に除去
      const shouldFilter = staffList.length > 0
      const removedNames = emptyRemovedStaff()
      const shiftAssignments: Record<string, ShiftPresetData_Input> = {}
      for (const [dateStr, shifts] of Object.entries(assignments)) {
        if (shouldFilter) {
          const sanitized = sanitizeShiftData(shifts, staffNamesByShift)
          shiftAssignments[dateStr] = { day: sanitized.day, evening: sanitized.evening, night: sanitized.night }
          mergeRemovedStaff(removedNames, sanitized.removed)
        } else {
          shiftAssignments[dateStr] = {
            day: shifts.day ?? {},
            evening: shifts.evening ?? {},
            night: shifts.night ?? {},
          }
        }
      }
      notifyRemovedStaff(removedNames)

      const weekTasks = (excelPlanResponse.echoProductionTasks ?? [])
        .filter((t) => selectedWeek.dates.includes(t.date)) as unknown as ProductionTask_Input[]

      const futureTasks = (excelPlanResponse.echoProductionTasks ?? [])
        .filter((t) => t.date > selectedWeek.endDate) as unknown as ProductionTask_Input[]

      const pastTasks = collectPastTasks(excelPlanResponse.echoProductionTasks ?? [], selectedWeek.startDate)

      const result = await DetailedOptimizerService.runDetailedOptimizer({
        production_tasks: weekTasks,
        orders: excelPlanResponse.echoOrders ?? [],
        products: productsMap,
        machines: [],
        staff_members: staffList,
        shift_assignments: shiftAssignments,
        planning_start: selectedWeek.startDate,
        planning_end: selectedWeek.endDate,
        future_tasks: futureTasks,
        past_tasks: pastTasks,
      })
      setOptimizerResult(result)
      setOptimizedAssignments(assignments)
      setStepIndex(3)
    } catch (error) {
      console.error('Optimizer failed:', error)
      toast?.show({ severity: 'error', summary: 'エラー', detail: '最適化に失敗しました', life: 4000 })
    } finally {
      setIsOptimizing(false)
    }
  }, [excelPlanResponse, selectedWeek, assignments, staffList, staffNamesByShift, notifyRemovedStaff, toast, buildLatestShiftData])

  const applyPreset = useCallback(
    (date: string, data: ShiftPresetData_Input) => {
      // staffList が未ロードの場合はフィルタをスキップ
      const shouldFilter = staffList.length > 0
      const sanitized = shouldFilter
        ? sanitizeShiftData(data, staffNamesByShift)
        : { day: data.day ?? {}, evening: data.evening ?? {}, night: data.night ?? {}, removed: emptyRemovedStaff() }
      setAssignments((prev) => ({
        ...prev,
        [date]: {
          day: { ...prev[date]?.day, ...sanitized.day },
          evening: { ...prev[date]?.evening, ...sanitized.evening },
          night: { ...prev[date]?.night, ...sanitized.night },
        },
      }))
      notifyRemovedStaff(sanitized.removed)
    },
    [staffList.length, staffNamesByShift, notifyRemovedStaff],
  )

  const clearDateAssignments = useCallback((date: string) => {
    setAssignments((prev) => ({
      ...prev,
      [date]: {
        day: {},
        evening: {},
        night: {},
      },
    }))
  }, [])

  const extractReloadErrorMessage = useCallback((error: unknown): string => {
    if (error && typeof error === 'object') {
      const err = error as { message?: string; body?: { detail?: string } }
      if (err.body?.detail) return String(err.body.detail)
      if (err.message) return err.message
    }
    return '不明なエラーが発生しました'
  }, [])

  // Reload Excel from OneDrive and re-run optimizer with current week & shifts
  const reloadAndReoptimize = useCallback(async () => {
    if (!excelPlanResponse || !selectedWeek) return

    resetExcelReloadState()
    setExcelReloadModalVisible(true)

    let currentStep: ExcelReloadStep = 'downloading'

    try {
      // 1. Download latest file from OneDrive
      setExcelReloadStage('downloading')
      const onedriveFileId = excelPlanResponse.fileMeta?.onedrive_file_id
      if (onedriveFileId) {
        await FilesService.downloadOnedriveFile({ file_id: onedriveFileId })
      }

      // 2. Re-read Excel data (force=1 to bypass cache)
      currentStep = 'reading'
      setExcelReloadStage(currentStep)
      const newPlanResponse = await ProductionPlanService.createProductionPlan(1)
      const updatedResponse: ExcelPlanResponse = {
        ...newPlanResponse,
        echoProducts: newPlanResponse.echoOrderProducts,
        echoOrders: newPlanResponse.echoOrders,
      }
      setExcelPlanResponse(updatedResponse)

      // 3. Re-run optimizer with same week & assignments but new data
      currentStep = 'optimizing'
      setExcelReloadStage(currentStep)
      const productsMap: Record<string, Product_Input> = {}
      for (const p of updatedResponse.echoOrderProducts ?? []) {
        productsMap[p.sku_id] = p as unknown as Product_Input
      }

      const shiftAssignments: Record<string, ShiftPresetData_Input> = {}
      for (const [dateStr, shifts] of Object.entries(assignments)) {
        shiftAssignments[dateStr] = {
          day: shifts.day ?? {},
          evening: shifts.evening ?? {},
          night: shifts.night ?? {},
        }
      }

      const weekTasks = (updatedResponse.echoProductionTasks ?? [])
        .filter((t) => selectedWeek.dates.includes(t.date)) as unknown as ProductionTask_Input[]

      const futureTasks = (updatedResponse.echoProductionTasks ?? [])
        .filter((t) => t.date > selectedWeek.endDate) as unknown as ProductionTask_Input[]

      const pastTasks = collectPastTasks(updatedResponse.echoProductionTasks ?? [], selectedWeek.startDate)

      const result = await DetailedOptimizerService.runDetailedOptimizer({
        production_tasks: weekTasks,
        orders: updatedResponse.echoOrders ?? [],
        products: productsMap,
        machines: [],
        staff_members: staffList,
        shift_assignments: shiftAssignments,
        planning_start: selectedWeek.startDate,
        planning_end: selectedWeek.endDate,
        future_tasks: futureTasks,
        past_tasks: pastTasks,
      })
      setOptimizerResult(result)
      setOptimizedAssignments(assignments)

      setExcelReloadStage('success')
      setExcelReloadFailedStep(null)
      setExcelReloadError(null)
      toast?.show({ severity: 'success', summary: '再読込完了', detail: 'Excelから再読込し、最適化を実行しました', life: 3000 })
    } catch (error) {
      const message = extractReloadErrorMessage(error)
      setExcelReloadFailedStep(currentStep)
      setExcelReloadStage('error')
      setExcelReloadError(message)
      console.error('Reload & reoptimize failed:', error)
      toast?.show({ severity: 'error', summary: '再読み込み失敗', detail: message, life: 5000 })
    }
  }, [excelPlanResponse, selectedWeek, assignments, staffList, toast, setExcelPlanResponse, resetExcelReloadState, extractReloadErrorMessage])

  return {
    stepIndex,
    steps,
    excelPlanResponse,
    selectedWeek,
    weekOptions,
    machineIds,
    staffList,
    staffQuery,
    assignments,
    optimizerResult,
    isOptimizing,
    goToStep,
    loadExcel,
    selectWeek,
    updateAssignment,
    updateMachineDayAssignments,
    hasPendingShiftChanges,
    optimizedAssignments,
    applyPreset,
    clearDateAssignments,
    runOptimizer,
    reloadAndReoptimize,
    excelReloadDialog: {
      visible: excelReloadModalVisible,
      open: reloadAndReoptimize,
      close: closeExcelReloadModal,
      progress: excelReloadProgress,
      stage: excelReloadStage,
      error: excelReloadError,
      isExcelReloading,
    } satisfies ExcelReloadDialogState,
    excelWriteDialog: {
      visible: excelWriteModalVisible,
      open: writeToExcel,
      close: closeExcelWriteModal,
      isWriting: excelWriteIsWriting,
      isLocked: excelWriteIsLocked,
      isSuccess: excelWriteIsSuccess,
      progress: excelWriteProgress,
      lastLog: excelWriteLastLog,
      error: excelWriteError,
    } satisfies ExcelWriteDialogState,
  }
}
